import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronRight } from "lucide-react";

export default function Exercises() {
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({ muscle_group: "All", equipment: "All", difficulty: "All", movement_pattern: "All" });

  useEffect(() => { api.get("/exercises/filters").then(({ data }) => setFilters(data)).catch(() => {}); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = { q: q || undefined };
      Object.entries(sel).forEach(([k, v]) => { if (v && v !== "All") params[k] = v; });
      api.get("/exercises", { params }).then(({ data }) => setExercises(data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, sel]);

  const FilterSelect = ({ field, label }) => {
    if (!(filters[field] || []).length) return null;
    return (
      <Select value={sel[field]} onValueChange={(v) => setSel((s) => ({ ...s, [field]: v }))}>
        <SelectTrigger data-testid={`filter-${field}`} className="bg-card border-border h-9 text-xs w-auto"><SelectValue placeholder={label} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="All">{label}: All</SelectItem>
          {(filters[field] || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  };

  return (
    <AppShell title="Video Library">
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="exercise-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exercises…" className="bg-card border-border pl-9 h-11" />
      </div>
      <div data-testid="exercise-video-filters" className="flex gap-2 overflow-x-auto rd-scrollbar pb-3">
        <FilterSelect field="muscle_group" label="Type" />
        <FilterSelect field="equipment" label="Equipment" />
        <FilterSelect field="movement_pattern" label="Pattern" />
        <FilterSelect field="difficulty" label="Level" />
      </div>

      <div className="space-y-2">
        {loading ? (
          [...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl bg-card" />)
        ) : exercises.length === 0 ? (
          <p className="text-muted-foreground text-center py-10">No exercises match your filters.</p>
        ) : (
          exercises.map((ex) => (
            <Card key={ex.id} data-testid={`exercise-card-${ex.id}`} onClick={() => navigate(`/exercises/${ex.id}`)} className="rounded-xl bg-card border-border p-3 cursor-pointer hover:border-[#ccff0c]/40 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{ex.name}</p>
                  <div className="flex gap-1.5 mt-1">
                    {ex.muscle_group && <Badge className="bg-secondary border border-border text-muted-foreground text-[10px]">{ex.muscle_group}</Badge>}
                    {ex.equipment && <Badge className="bg-secondary border border-border text-muted-foreground text-[10px]">{ex.equipment}</Badge>}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </Card>
          ))
        )}
      </div>
    </AppShell>
  );
}
